import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import useCompanyService from "../../database/CompanyService";
import useProductService from "../../database/ProductService";
import useProductKeyService from "../../database/ProductKeyService";
import useProductKeyTransferService from "../../database/ProductKeyTransferService";
import useVoucherService from "../../database/VoucherService";
import { Company } from "../../models/Company";
import { Product } from "../../models/Product";
import { ProductKeyTransfer } from "../../models/ProductKeyTransfer";
import { auth } from "../../firebase";
import { Timestamp } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';

/* Cap on how many key strings are stored on a single history record, so a very large
   transfer cannot push the document past Firestore's 1 MiB limit. */
const maximumLoggedKeys = 1000;

/* How many history records the page shows. */
const transferHistoryPageSize = 25;

export interface KeyTransferPageState {
    isLoading: boolean;

    availableCompanies: Company[];

    sourceCompany: Company | null;
    sourceProducts: Product[];
    sourceProduct: Product | null;
    sourceAvailableKeyCount: number;
    sourceOutstandingVoucherCount: number;

    destinationCompany: Company | null;
    destinationProducts: Product[];
    destinationProduct: Product | null;
    destinationAvailableKeyCount: number;
    destinationOutstandingVoucherCount: number;

    requestedNumberOfKeys: number;

    transferError: string | null;
    transferResultMessage: string | null;

    transferHistory: ProductKeyTransfer[];
    transferHistoryError: string | null;

    loadError: string | null;
}

const initialState: KeyTransferPageState = {
    isLoading: false,

    availableCompanies: [],

    sourceCompany: null,
    sourceProducts: [],
    sourceProduct: null,
    sourceAvailableKeyCount: 0,
    sourceOutstandingVoucherCount: 0,

    destinationCompany: null,
    destinationProducts: [],
    destinationProduct: null,
    destinationAvailableKeyCount: 0,
    destinationOutstandingVoucherCount: 0,

    requestedNumberOfKeys: 0,

    transferError: null,
    transferResultMessage: null,

    transferHistory: [],
    transferHistoryError: null,

    loadError: null,
}

export const KeyTransferPageSlice = createSlice({
    name: 'keyTransferPage',
    initialState,
    reducers: {
        setLoadingState: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setCompanies: (state, action: PayloadAction<Company[]>) => {
            state.availableCompanies = action.payload;
        },

        setSourceCompany: (state, action: PayloadAction<Company | null>) => {
            state.sourceCompany = action.payload;
        },
        setSourceProducts: (state, action: PayloadAction<Product[]>) => {
            state.sourceProducts = action.payload;
        },
        setSourceProduct: (state, action: PayloadAction<Product | null>) => {
            state.sourceProduct = action.payload;
        },
        setSourceCounts: (state, action: PayloadAction<{ availableKeys: number, outstandingVouchers: number }>) => {
            state.sourceAvailableKeyCount = action.payload.availableKeys;
            state.sourceOutstandingVoucherCount = action.payload.outstandingVouchers;
        },

        setDestinationCompany: (state, action: PayloadAction<Company | null>) => {
            state.destinationCompany = action.payload;
        },
        setDestinationProducts: (state, action: PayloadAction<Product[]>) => {
            state.destinationProducts = action.payload;
        },
        setDestinationProduct: (state, action: PayloadAction<Product | null>) => {
            state.destinationProduct = action.payload;
        },
        setDestinationCounts: (state, action: PayloadAction<{ availableKeys: number, outstandingVouchers: number }>) => {
            state.destinationAvailableKeyCount = action.payload.availableKeys;
            state.destinationOutstandingVoucherCount = action.payload.outstandingVouchers;
        },

        setRequestedNumberOfKeys: (state, action: PayloadAction<number>) => {
            state.requestedNumberOfKeys = action.payload;
        },
        setTransferError: (state, action: PayloadAction<string | null>) => {
            state.transferError = action.payload;
        },
        setTransferResultMessage: (state, action: PayloadAction<string | null>) => {
            state.transferResultMessage = action.payload;
        },
        setTransferHistory: (state, action: PayloadAction<ProductKeyTransfer[]>) => {
            state.transferHistory = action.payload;
        },
        setTransferHistoryError: (state, action: PayloadAction<string | null>) => {
            state.transferHistoryError = action.payload;
        },
        setLoadError: (state, action: PayloadAction<string | null>) => {
            state.loadError = action.payload;
        },
    }
});

/* The services are passed in rather than resolved here: their factories are named
   like React hooks, so calling them outside a thunk trips the rules-of-hooks lint. */
async function loadCountsAsync(
    productKeyService: ReturnType<typeof useProductKeyService>,
    voucherService: ReturnType<typeof useVoucherService>,
    companyGuid: string,
    productGuid: string) {

    var availableKeys = await productKeyService.getRedeemableProductKeyCountAsync(companyGuid, productGuid);
    var outstandingVouchers = await voucherService.getUnassignedVoucherCountAsync(companyGuid, productGuid);

    return { availableKeys, outstandingVouchers };
}

export const keyTransferPageInitialLoadAsync = createAsyncThunk<void, void, { state: RootState }>(
    'keyTransferPage/initialLoadAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {

        dispatch(setLoadingState(true));
        dispatch(setLoadError(null));

        /* Without this the page sits on the spinner forever when the read fails,
           which is exactly what a signed-out or unauthorised session gets. */
        try {
            var companyService = useCompanyService();
            var companies = await companyService.getCompaniesAsync();
            dispatch(setCompanies(companies));

            await dispatch(loadTransferHistoryAsync());
        } catch (error) {
            dispatch(setLoadError(`Could not load companies: ${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

export const loadTransferHistoryAsync = createAsyncThunk<void, void, { state: RootState }>(
    'keyTransferPage/loadTransferHistoryAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {

        dispatch(setTransferHistoryError(null));

        try {
            var productKeyTransferService = useProductKeyTransferService();
            var transfers = await productKeyTransferService.getTransfersAsync(transferHistoryPageSize);
            dispatch(setTransferHistory(transfers));
        } catch (error) {
            /* Never let a history read failure look like a transfer failure. */
            dispatch(setTransferHistoryError(`Could not load transfer history: ${error}`));
        }

        return;
    }
);

export const setSourceCompanyAsync = createAsyncThunk<void, Company, { state: RootState }>(
    'keyTransferPage/setSourceCompanyAsync',
    async (company: Company, { dispatch, getState, rejectWithValue }) => {

        dispatch(setSourceCompany(company));
        dispatch(setSourceProduct(null));
        dispatch(setSourceCounts({ availableKeys: 0, outstandingVouchers: 0 }));
        dispatch(clearTransferFeedback());

        dispatch(setLoadingState(true));

        try {
            var productService = useProductService();
            var products = await productService.getProductsByCompanyGuid(company.guid);
            dispatch(setSourceProducts(products));
        } catch (error) {
            dispatch(setLoadError(`Could not load products: ${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

export const setSourceProductAsync = createAsyncThunk<void, Product, { state: RootState }>(
    'keyTransferPage/setSourceProductAsync',
    async (product: Product, { dispatch, getState, rejectWithValue }) => {

        const state = getState().keyTransferPage;
        if (state.sourceCompany == null) return;

        dispatch(setSourceProduct(product));
        dispatch(clearTransferFeedback());

        dispatch(setLoadingState(true));

        try {
            var counts = await loadCountsAsync(useProductKeyService(), useVoucherService(), state.sourceCompany.guid, product.guid);
            dispatch(setSourceCounts(counts));
        } catch (error) {
            dispatch(setLoadError(`Could not load counts: ${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

export const setDestinationCompanyAsync = createAsyncThunk<void, Company, { state: RootState }>(
    'keyTransferPage/setDestinationCompanyAsync',
    async (company: Company, { dispatch, getState, rejectWithValue }) => {

        dispatch(setDestinationCompany(company));
        dispatch(setDestinationProduct(null));
        dispatch(setDestinationCounts({ availableKeys: 0, outstandingVouchers: 0 }));
        dispatch(clearTransferFeedback());

        dispatch(setLoadingState(true));

        try {
            var productService = useProductService();
            var products = await productService.getProductsByCompanyGuid(company.guid);
            dispatch(setDestinationProducts(products));
        } catch (error) {
            dispatch(setLoadError(`Could not load products: ${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

export const setDestinationProductAsync = createAsyncThunk<void, Product, { state: RootState }>(
    'keyTransferPage/setDestinationProductAsync',
    async (product: Product, { dispatch, getState, rejectWithValue }) => {

        const state = getState().keyTransferPage;
        if (state.destinationCompany == null) return;

        dispatch(setDestinationProduct(product));
        dispatch(clearTransferFeedback());

        dispatch(setLoadingState(true));

        try {
            var counts = await loadCountsAsync(useProductKeyService(), useVoucherService(), state.destinationCompany.guid, product.guid);
            dispatch(setDestinationCounts(counts));
        } catch (error) {
            dispatch(setLoadError(`Could not load counts: ${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

export const transferKeysAsync = createAsyncThunk<void, void, { state: RootState }>(
    'keyTransferPage/transferKeysAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {

        const state = getState().keyTransferPage;

        dispatch(setTransferError(null));
        dispatch(setTransferResultMessage(null));

        const sourceCompany = state.sourceCompany;
        const sourceProduct = state.sourceProduct;
        const destinationCompany = state.destinationCompany;
        const destinationProduct = state.destinationProduct;

        if (sourceCompany == null || sourceProduct == null || destinationCompany == null || destinationProduct == null) {
            dispatch(setTransferError("Choose a source and a destination product first."));
            return;
        }

        if (sourceProduct.guid === destinationProduct.guid) {
            dispatch(setTransferError("The source and destination product are the same."));
            return;
        }

        if (state.requestedNumberOfKeys < 1) {
            dispatch(setTransferError("Enter how many keys to transfer."));
            return;
        }

        if (state.requestedNumberOfKeys > state.sourceAvailableKeyCount) {
            dispatch(setTransferError(`Only ${state.sourceAvailableKeyCount} key(s) are available to transfer from ${sourceCompany.name} ${sourceProduct.name}.`));
            return;
        }

        /* Moving stock between companies has commercial consequences, so make the
           admin confirm exactly what is about to happen. */
        const confirmed = window.confirm(
            `Transfer ${state.requestedNumberOfKeys} product key(s)?\n\n` +
            `From: ${sourceCompany.name} - ${sourceProduct.name}\n` +
            `To: ${destinationCompany.name} - ${destinationProduct.name}\n\n` +
            `${sourceCompany.name} will be left with ${state.sourceAvailableKeyCount - state.requestedNumberOfKeys} available key(s) ` +
            `against ${state.sourceOutstandingVoucherCount} outstanding voucher(s).`
        );

        if (!confirmed) return;

        dispatch(setLoadingState(true));

        try {
            var productKeyService = useProductKeyService();

            var transferredKeys = await productKeyService.transferRedeemableProductKeysAsync(
                sourceCompany.guid,
                sourceProduct.guid,
                destinationCompany.guid,
                destinationProduct.guid,
                state.requestedNumberOfKeys);

            var transferredCount = transferredKeys.length;

            if (transferredCount < state.requestedNumberOfKeys) {
                dispatch(setTransferResultMessage(
                    `Transferred ${transferredCount} of ${state.requestedNumberOfKeys} key(s). ` +
                    `The rest were claimed by a redemption while the transfer ran, or are no longer available.`));
            } else {
                dispatch(setTransferResultMessage(
                    `Transferred ${transferredCount} key(s) from ${sourceCompany.name} - ${sourceProduct.name} to ${destinationCompany.name} - ${destinationProduct.name}.`));
            }

            if (transferredCount > 0) {
                /* The keys have already moved at this point, so a failure to write the
                   history record must not be reported as a failed transfer. */
                try {
                    var productKeyTransferService = useProductKeyTransferService();

                    var transferRecord = new ProductKeyTransfer(
                        uuidv4(),
                        sourceCompany.guid,
                        sourceCompany.name,
                        sourceProduct.guid,
                        sourceProduct.name,
                        destinationCompany.guid,
                        destinationCompany.name,
                        destinationProduct.guid,
                        destinationProduct.name,
                        state.requestedNumberOfKeys,
                        transferredCount,
                        transferredKeys.slice(0, maximumLoggedKeys),
                        transferredCount > maximumLoggedKeys,
                        auth.currentUser?.email ?? null,
                        Timestamp.now().toDate());

                    await productKeyTransferService.addTransferAsync(transferRecord);
                    await dispatch(loadTransferHistoryAsync());
                } catch (error) {
                    dispatch(setTransferHistoryError(
                        `The keys were transferred, but the history record could not be saved: ${error}`));
                }
            }

            /* Refresh both sides so the admin sees the new position. */
            var voucherService = useVoucherService();

            var sourceCounts = await loadCountsAsync(productKeyService, voucherService, sourceCompany.guid, sourceProduct.guid);
            dispatch(setSourceCounts(sourceCounts));

            var destinationCounts = await loadCountsAsync(productKeyService, voucherService, destinationCompany.guid, destinationProduct.guid);
            dispatch(setDestinationCounts(destinationCounts));

            dispatch(setRequestedNumberOfKeys(0));
        } catch (error) {
            dispatch(setTransferError(`${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

function clearTransferFeedback() {
    return (dispatch: any) => {
        dispatch(setTransferError(null));
        dispatch(setTransferResultMessage(null));
    };
}

export const {
    setLoadingState,
    setCompanies,
    setSourceCompany,
    setSourceProducts,
    setSourceProduct,
    setSourceCounts,
    setDestinationCompany,
    setDestinationProducts,
    setDestinationProduct,
    setDestinationCounts,
    setRequestedNumberOfKeys,
    setTransferError,
    setTransferResultMessage,
    setTransferHistory,
    setTransferHistoryError,
    setLoadError,
} = KeyTransferPageSlice.actions;

export default KeyTransferPageSlice.reducer;

export const selectState = (state: RootState) => state.keyTransferPage;
